import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE, formatApiError, useAuth } from "../components/AuthContext";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Building2, Sparkles, ArrowRight, Copy } from "lucide-react";

const ERROR_LABELS = {
  invite_not_found: "This invitation link is invalid. Ask your admin to resend it.",
  invite_already_accepted: "This invitation has already been accepted. Try signing in.",
  invite_revoked: "This invitation was revoked. Ask your admin for a new one.",
  invite_expired: "This invitation has expired. Ask your admin for a new one.",
  email_already_registered: "An account with this email already exists. Try signing in.",
  invalid_totp_code: "The 6-digit code didn't match. Try scanning the QR code again.",
};

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [peek, setPeek] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [enable2fa, setEnable2fa] = useState(true);   // recommended default
  const [totpSetup, setTotpSetup] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  // Peek the invite to show org branding before they sign up
  useEffect(() => {
    let cancel = false;
    axios.get(`${API_BASE}/team/invite/${token}/peek`)
      .then((r) => { if (!cancel) { setPeek(r.data); setName(r.data.name || ""); } })
      .catch((e) => {
        const code = e?.response?.data?.detail;
        setError(ERROR_LABELS[code] || formatApiError(e));
      });
    return () => { cancel = true; };
  }, [token]);

  // Lazy-load TOTP setup the moment the user toggles 2FA on
  useEffect(() => {
    if (!enable2fa || totpSetup) return;
    axios.get(`${API_BASE}/team/invite/${token}/totp/setup`)
      .then((r) => setTotpSetup(r.data))
      .catch(() => {});
  }, [enable2fa, totpSetup, token]);

  const submit = async () => {
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirmPw) {
      toast.error("Passwords don't match");
      return;
    }
    if (enable2fa && (!totpCode || totpCode.length !== 6)) {
      toast.error("Enter the 6-digit code from your authenticator app");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_BASE}/team/invite/${token}/accept`, {
        name, password,
        totp_secret: enable2fa ? totpSetup?.secret : undefined,
        totp_code: enable2fa ? totpCode : undefined,
      });
      // Show recovery codes BEFORE bouncing to dashboard (only chance to view)
      if (r.data?.totp_recovery_codes?.length) {
        setRecoveryCodes(r.data.totp_recovery_codes);
      } else {
        refresh && refresh();
        navigate("/dashboard");
      }
    } catch (e) {
      const code = e?.response?.data?.detail;
      toast.error(ERROR_LABELS[code] || formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const continueToDashboard = () => {
    refresh && refresh();
    navigate("/dashboard");
  };

  if (error) {
    return (
      <CenteredCard>
        <h1 className="font-heading text-2xl mb-2" style={{ fontWeight: 500 }}>Can't accept invitation</h1>
        <p className="text-[var(--ink-2)] text-[14px]">{error}</p>
      </CenteredCard>
    );
  }

  if (!peek) {
    return (
      <CenteredCard>
        <Loader2 className="animate-spin inline mr-2" size={14} />
        Loading invitation…
      </CenteredCard>
    );
  }

  if (recoveryCodes) {
    return (
      <CenteredCard>
        <div className="overline mb-2 inline-flex items-center gap-1.5">
          <ShieldCheck size={12} /> Save these codes — final step
        </div>
        <h1 className="font-heading text-2xl mb-2" style={{ fontWeight: 500 }}>2FA recovery codes</h1>
        <p className="text-[13px] text-[var(--ink-2)] mb-4">
          If you lose your authenticator app, you can sign in with one of these codes (each works once).
          <b> This is the only time you'll see them — save them in your password manager now.</b>
        </p>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {recoveryCodes.map((c) => (
            <code key={c} className="bg-[var(--subtle)] p-2 rounded text-center font-mono text-[13px]" data-testid={`recovery-code-${c}`}>{c}</code>
          ))}
        </div>
        <div className="flex justify-between items-center">
          <button
            onClick={() => { navigator.clipboard.writeText(recoveryCodes.join("\n")); toast.success("Codes copied"); }}
            className="btn-secondary text-[12px]"
            data-testid="recovery-copy-btn"
          >
            <Copy size={11} /> Copy all
          </button>
          <button onClick={continueToDashboard} className="btn-primary" data-testid="recovery-continue-btn">
            I've saved them — continue <ArrowRight size={14} />
          </button>
        </div>
      </CenteredCard>
    );
  }

  const brand = peek.org_brand_color || "#c0492a";
  return (
    <CenteredCard>
      <div className="text-center mb-6">
        <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
             style={{ background: brand, color: "#fff" }}>
          <Building2 size={20} />
        </div>
        <div className="overline mb-1">{peek.org_name}</div>
        <h1 className="font-heading text-2xl" style={{ fontWeight: 500 }}>
          Join as a <em className="italic" style={{ color: brand }}>{peek.role}</em>
        </h1>
        <p className="text-[13px] text-[var(--ink-2)] mt-2">
          Invited by {peek.inviter_name} — set up your password and (optionally) 2FA to get started.
        </p>
      </div>

      <div className="space-y-4">
        <FieldStatic label="Email">{peek.email}</FieldStatic>
        <Field label="Full name" value={name} onChange={setName} testId="accept-name" />
        <Field label="Choose a password" type="password" value={password} onChange={setPassword}
               testId="accept-password" placeholder="At least 8 characters" />
        <Field label="Confirm password" type="password" value={confirmPw} onChange={setConfirmPw}
               testId="accept-confirm-password" />

        <label className="card p-4 flex items-start gap-3 cursor-pointer" data-testid="accept-2fa-toggle">
          <input type="checkbox" checked={enable2fa} onChange={(e) => setEnable2fa(e.target.checked)}
                 className="mt-1" />
          <div className="flex-1">
            <div className="font-medium text-[13px] inline-flex items-center gap-1.5">
              <ShieldCheck size={13} /> Enable two-factor authentication (recommended)
            </div>
            <div className="text-[12px] text-[var(--ink-3)] mt-1">
              Add an extra layer of security using an authenticator app (Google Authenticator, 1Password, Authy).
            </div>
          </div>
        </label>

        {enable2fa && totpSetup && (
          <div className="card p-4 space-y-3" data-testid="accept-2fa-setup">
            <div className="text-[12px] font-medium">Scan this QR code or paste the secret manually:</div>
            <div className="flex items-start gap-4">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=130x130&data=${encodeURIComponent(totpSetup.qr_uri)}`}
                alt="2FA QR code"
                className="border border-[var(--subtle-2)] rounded"
                data-testid="accept-2fa-qr"
              />
              <div className="flex-1 text-[11px]">
                <div className="text-[var(--ink-3)] mb-1">Or paste this secret:</div>
                <code className="bg-[var(--subtle)] p-2 rounded font-mono text-[11px] block break-all"
                      data-testid="accept-2fa-secret">{totpSetup.secret}</code>
              </div>
            </div>
            <Field
              label="Enter the 6-digit code from your app" value={totpCode} onChange={(v) => setTotpCode(v.replace(/\D/g, "").slice(0, 6))}
              testId="accept-2fa-code" placeholder="123456" />
          </div>
        )}

        <button
          onClick={submit}
          disabled={submitting || !name || !password || !confirmPw}
          className="btn-primary w-full justify-center"
          style={enable2fa ? {} : { background: brand }}
          data-testid="accept-submit-btn"
        >
          {submitting ? <><Loader2 className="animate-spin" size={14} /> Setting up…</>
                      : <>Accept invitation <Sparkles size={14} /></>}
        </button>
      </div>
    </CenteredCard>
  );
}

function CenteredCard({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[var(--bg)]">
      <div className="card p-8 max-w-lg w-full" data-testid="accept-invite-card">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, placeholder }) {
  return (
    <div>
      <label className="block text-[12px] text-[var(--ink-3)] mb-1.5">{label}</label>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} className="input w-full" data-testid={testId}
      />
    </div>
  );
}

function FieldStatic({ label, children }) {
  return (
    <div>
      <label className="block text-[12px] text-[var(--ink-3)] mb-1.5">{label}</label>
      <div className="input w-full bg-[var(--subtle)]">{children}</div>
    </div>
  );
}
